import { useState, memo, useEffect, useMemo, useCallback, useContext } from "react"
import { DgtPdOrderPicklist, DgtPdOrderPicklistMovement, WasteRecordStatus } from "../../../models"
import { AiFillCheckCircle } from 'react-icons/ai'
import { MdBlock } from 'react-icons/md'
import { GoChecklist } from 'react-icons/go'
import { RiBarcodeLine } from 'react-icons/ri'
import { formatDateFromUnix, formatNumberV2, ceil2Decimal } from '../../../utilities/Format'
import Badge from "../../../components/Badge"
import DataGrid, { RowsChangeData } from 'react-data-grid'
import { 
  PicklistItemRecordColumn,
  PicklistItemRecordColumnReadOnly,
  SummaryRow,
  PicklistItemDeleteColumn
} from '../../../constants/Column'
import {
  OrderPicklistMovement,
  RecordForm
} from '../../../components/types'
import Alert from '../../../components/Alert'
import { useForm, useFieldArray } from 'react-hook-form'
import RecordPicklistDialogConf from '../RecordPicklistDialogConf'

import {
  pdRecordGoodWasteBulkByBatch,
  pdRecordCompletedPicklist,
  updateWasteRecordedStatus,
  cancelMovement,
  reOpenMovement
} from '../../../states/PicklistAction'

import NotFound404 from "../../../components/NotFound404"
import { notifyToQCLine } from '../../../apis'
import { Badge as AmplifyBadge } from '@aws-amplify/ui-react';

import MovementContext from "../../../contexts/MovementContext"
import PicklistContext from "../../../contexts/PicklistContext"
interface RecordPicklistProps {
  header: DgtPdOrderPicklist,
  materialItems : DgtPdOrderPicklist[]
  username: string
  isAdmin?: boolean
  readOnly?: boolean
}

const RecordPicklist = ({ header, materialItems, username, isAdmin = false, readOnly = false } : RecordPicklistProps) => {
  const [ showCompleteDialog, setShowCompleteDialog ] = useState<boolean>(false)

  const uniqueItems = materialItems.filter((value, index, self) => 
    self.findIndex(s => s.materialCode === value.materialCode) === index
  )
  uniqueItems.sort((a, b) => a.materialType.localeCompare(b.materialType))

  const matCodeDefault = uniqueItems.length > 0 ? uniqueItems[0].materialCode : ''
  
  const [ currentMatCode, setCurrentMatCode ] = useState<string>(matCodeDefault)
  const [showToastError, setShowToastError] = useState<boolean>(false);

  const currentMaterial = uniqueItems.find(item => item.materialCode === currentMatCode)

  const {
    control
  } = useForm<RecordForm>({
    defaultValues: {
      usedOfMovements: []
    }
  })

  const { replace, fields } = useFieldArray<RecordForm>({
    control, name: 'usedOfMovements'
  })

  const picklistContext = useContext(PicklistContext);
  const movementContext = useContext(MovementContext);

  useEffect(() => {
    const mvsByMat = movementContext?.movements.filter(itm => itm.materialCode === currentMatCode)
    replace(mvsByMat || [])
  }, [ movementContext, currentMatCode, replace ])

  const summaryMovementRows = useMemo(() => {
    const summaryRow : SummaryRow = { 
      total: ceil2Decimal(fields.reduce((prev, { amount }) => prev + amount, 0)),
      totalRequest: ceil2Decimal(fields.reduce((prev, { materialRequestQty }) => prev + (materialRequestQty || 0), 0)),
      totalUsedGood: ceil2Decimal(fields.reduce((prev, { usedGoodQty }) => prev + (usedGoodQty || 0), 0)),
      totalUsedWaste: ceil2Decimal(fields.reduce((prev, { usedWasteQty }) => prev + (usedWasteQty || 0), 0)),
      totalReturnGood: ceil2Decimal(fields.reduce((prev, { returnGoodQty }) => prev + (returnGoodQty || 0), 0)),
      totalReturnGoodShrink: ceil2Decimal(fields.reduce((prev, { returnGoodShrinkQty }) => prev + (returnGoodShrinkQty || 0), 0)),
      totalReturnWaste: ceil2Decimal(fields.reduce((prev, { returnWasteQty }) => prev + (returnWasteQty || 0), 0)),
      totalOtherQty: ceil2Decimal(fields.reduce((prev, { otherQty }) => prev + (otherQty || 0), 0)),
      unit: fields.length > 0 ? fields[0].materialUnit : ''
    }
    return [ summaryRow ];
  }, [ fields ]);

  const onRowsChange = async (rows: OrderPicklistMovement[], 
    changedInfo: RowsChangeData<OrderPicklistMovement, SummaryRow>) => {
    const changedRowIdx = changedInfo.indexes[0]
    const changedRows = [...rows]

    const row = changedRows[changedRowIdx]

    if (changedInfo.column.key === 'inactive') {
      if (row.canceledDateTime! > 0) {
        const updated = await cancelMovement(row.orderId, row.sk, username, row._version || 1)
        movementContext?.mutateMovements([updated])
      } else {
        const updated = await reOpenMovement(row.orderId, row.sk, username, row._version || 1)
        movementContext?.mutateMovements([updated])
      }
      return
    }

    let totalGood: number = 0
    let totalOtherQty: number = 0
    let totalWaste: number = 0
    let returnGood: number = 0
    let returnGoodShrink: number = 0
    let returnWaste: number = 0
    let returnReasonCode = null
    let returnReasonDescription = null

    if (changedInfo.column.key === 'usedGoodQty') {
      totalGood = Number(row.usedGoodQty)
      totalOtherQty = Number(row.otherQty)
      totalWaste = Number(row.usedWasteQty)
      if (totalGood === totalWaste) totalWaste = 0
      returnGood = totalGood > 0 ? row.amount - totalGood - totalOtherQty - totalWaste : NaN
    } else if (changedInfo.column.key === 'usedWasteQty') {
      totalWaste = Number(row.usedWasteQty)
      totalOtherQty = Number(row.otherQty)
      totalGood = Number(row.usedGoodQty)
      if (totalGood === totalWaste) totalGood = 0
      returnGood = Number((row.amount - totalGood - totalOtherQty - totalWaste).toFixed(3))
    } else if (changedInfo.column.key === 'returnGoodQty') {
      returnGood = Number(row.returnGoodQty)
      totalWaste = Number(row.usedWasteQty)
      totalOtherQty = Number(row.otherQty)
      totalGood = row.amount - totalWaste - totalOtherQty - returnGood
    } else if (changedInfo.column.key === 'returnGoodShrinkQty') {
      returnGoodShrink = Number(row.returnGoodShrinkQty)
      returnGood = Number(row.returnGoodQty?.toFixed(3)) - returnGoodShrink - returnWaste
      totalWaste = Number(row.usedWasteQty)
      totalOtherQty = Number(row.otherQty)
      totalGood = Number(row.usedGoodQty)
    }
    else if (changedInfo.column.key === 'returnWasteQty') {
      returnWaste = Number(row.returnWasteQty)
      returnGood = Number(row.returnGoodQty?.toFixed(3)) - returnWaste
      totalWaste = Number(row.usedWasteQty)
      totalOtherQty = Number(row.otherQty)
      totalGood = Number(row.usedGoodQty)
      returnGoodShrink = Number(row.returnGoodShrinkQty)
    }
    else if (changedInfo.column.key === 'otherQty') {
      totalWaste = Number(row.usedWasteQty)
      totalGood = Number(row.usedGoodQty)
      totalOtherQty = Number(row.otherQty)
      if (totalGood === totalOtherQty) totalGood = 0
      returnGood = Number((row.amount - totalGood - totalWaste - totalOtherQty).toFixed(3))
    } else if (changedInfo.column.key === 'returnReasonCode') {
      totalGood = Number(row.usedGoodQty)
      totalOtherQty = Number(row.otherQty)
      totalWaste = Number(row.usedWasteQty)
      returnGood = Number(row.returnGoodQty)
      returnGoodShrink = Number(row.returnGoodShrinkQty)
      returnWaste = Number(row.returnWasteQty)
      returnReasonCode = row.returnReasonCode
      returnReasonDescription = row.returnReasonDescription
    }

    if (totalGood < 0) return
    if (totalOtherQty < 0) return
    if (totalWaste < 0) return
    if (returnGood < 0) {
      setShowToastError(true)
      setTimeout(() => setShowToastError(false), 3000)
      return
    }
    if (returnGoodShrink < 0) returnGoodShrink = 0
    if (returnWaste < 0) return
    
    const amountWithdraw = Number(row.amount)
    changedRows[changedRowIdx] = { 
      ...row,
      amount: amountWithdraw,
      usedGoodQty: totalGood,
      usedWasteQty: totalWaste,
      returnGoodQty: returnGood,
      returnGoodShrinkQty: returnGoodShrink < 0 ? 0 : returnGoodShrink,
      returnWasteQty: returnWaste,
      otherQty: totalOtherQty,
      wasteRecordedStatus: WasteRecordStatus.PD_PENDING,
      returnReasonCode,
      returnReasonDescription
    }

    const changedMovements = changedRows.filter(mv => mv.amount > 0 && (mv.usedGoodQty! > 0 || mv.returnGoodQty! > 0 || mv.returnGoodShrinkQty! > 0 || mv.usedWasteQty! > 0 || mv.returnWasteQty! > 0 || mv.otherQty! > 0))
    if (changedMovements.length > 0) {
      const updatedMvs = await pdRecordGoodWasteBulkByBatch(changedMovements, username)
      movementContext?.mutateMovements(updatedMvs)
    } else {
      movementContext?.mutateMovements(changedRows)
    } 
  }

  const onCompleted = useCallback(async (wasteMvs: DgtPdOrderPicklistMovement[]) => {
    const updatedHeader = await pdRecordCompletedPicklist(header, username)
    // note - change to alert pkg only
    const pkgWasteMvs = wasteMvs.filter(mv => mv.materialType === 'PKG')
    if (pkgWasteMvs.length > 0) {
      await updateWasteRecordedStatus(wasteMvs, WasteRecordStatus.PD_CONFIRMED, true, WasteRecordStatus.QC_APPROVED)
      // note - call synchonous for smooth ux
      notifyToQCLine(header.pk, header.division!, JSON.stringify(pkgWasteMvs.map(mv => (
        {
          materialCode: mv.materialCode,
          materialDescription: mv.materialDescription,
          materialUnit: mv.materialUnit,
          batchLotNo: mv.batchLotNo,
          returnWasteQty: mv.returnWasteQty,
        }
      ))))
    }
    picklistContext?.mutateHeader(updatedHeader)
    setShowCompleteDialog(false)
  }, [ header, username, picklistContext ])

  const onUsedGoodAll = async () => {
    const allGoodMvs = [...fields].map(
      mv => {
        const totalGood = mv.amount
        return {...mv, 
          usedGoodQty: totalGood,
          otherQty: 0,
          usedWasteQty: 0,
          returnGoodShrinkQty: 0,
          returnWasteQty: 0,
          returnGoodQty: 0
        }
      }
    )
    
    const changedMovements = allGoodMvs.filter(mv => mv.amount > 0 && mv.usedGoodQty! > 0)
    if (changedMovements.length > 0) {
      const updatedMvs = await pdRecordGoodWasteBulkByBatch(changedMovements, username)
      movementContext?.mutateMovements(updatedMvs)
    } else {
      movementContext?.mutateMovements(allGoodMvs)
    }
  }

  const displayTotalRequest = currentMaterial?.materialType === 'BULK' ? '' : formatNumberV2(currentMaterial?.requestQty)

  let columns = Array.from(readOnly ? PicklistItemRecordColumnReadOnly : PicklistItemRecordColumn)
  if (isAdmin && !readOnly) {
    columns.push(PicklistItemDeleteColumn)
  }

  return materialItems.length > 0 ? <>
    <div className='pb-5 mt-2 mb-1 space-y-5 '>
      <div>
        <button className="w-full mb-2 btn-success" onClick={() => setShowCompleteDialog(true)} disabled={readOnly}>
          <GoChecklist className="mr-1"/>สรุปยอดทั้งหมด
        </button>
        <div className={`${header?.recordedBy ? 'flex':'hidden'} gap-1 justify-center items-center mb-2`}>
          <AiFillCheckCircle size={22} className='text-green-600'/>
          <small>ยืนยันยอดโดย {header?.recordedBy} / {formatDateFromUnix(header?.recordedDateTime)}</small>
        </div>
        <div className="grid grid-cols-2 gap-1 md:grid-cols-3 lg:grid-cols-5 2xl:grid-cols-6 ">
          {uniqueItems.map(item => 
            <button key={item.materialCode}
              className={` p-1.5 text-left rounded-md border${currentMatCode === item.materialCode ? ' bg-blue-100 border-blue-600':''}`}
              onClick={() => setCurrentMatCode(item.materialCode)}>
                <div className="flex flex-col break-words">
                  <h6 className={`${currentMatCode === item.materialCode ? 'text-blue-600':''} text-sm`}>{item.materialDescription}</h6>
                  <div className="flex justify-between">
                    <h6 className='text-sm font-light text-gray-500'>{item.materialCode}</h6>
                    <div className="flex gap-1">
                      <Badge variant={` ${item.materialType === 'PKG' && item.requestQty === 0 ? '':'hidden'} bg-red-600 text-xs text-white h-fit`}>นอก BOM</Badge>
                      <Badge variant={`${item.materialType === 'PKG' ? 'bg-amber-600':'bg-blue-600'} text-xs text-white h-fit`}>{item.materialType}</Badge>
                      {item.barcodePkg && <Badge variant="bg-green-500 h-fit"><RiBarcodeLine color="white" /></Badge>}
                    </div>
                  </div>
                </div>
            </button>
          )}
        </div>
      </div>
      <div>
        <div className="flex justify-between pb-3">
          <div className="flex gap-2 h-fit">
            <h5>{currentMaterial?.materialCode} - {currentMaterial?.materialDescription}</h5>
            <h5 className="font-bold text-green-700 after:content-['รายการ'] after:font-normal after:text-black after:pl-1">{fields.length}</h5>
            /
            <h5 className="font-bold text-green-700 before:content-['รวม'] before:mr-2 before:text-black before:font-normal">{displayTotalRequest} {currentMaterial?.materialUnit}</h5>
            {currentMaterial?.barcodePkg && <AmplifyBadge size='small' variation="success"><RiBarcodeLine />{currentMaterial?.barcodePkg}</AmplifyBadge>}
          </div>
          <button type="button" className=" btn-outline-primary" onClick={onUsedGoodAll} disabled={readOnly}>
            ใช้ดีทั้งหมด ?
          </button>
        </div>
        <DataGrid
          style={{ height: 'fit-content' }}
          columns={ columns }
          rows={fields}
          defaultColumnOptions={{ resizable: true }}
          summaryRows={summaryMovementRows}
          onRowsChange={onRowsChange}
          rowClass={row => row.recordedBy ? ' bg-green-100 bg-opacity-50' : (
            row.canceledDateTime ? 'deprecated-item-row':''
          )}
          rowHeight={50}
        />
        <p className="pt-2 mt-5 text-sm italic text-right">หมายเหตุ 1 : ช่อง "คืนดีติดSH/STK" สำหรับยอดขวดติดShrink/Sticker แทนการพิมพ์ Comment</p>
        <p className="pt-2 mb-5 text-sm italic text-right">หมายเหตุ 2 : ช่อง "โอนถ่าย" สำหรับยอดโอนถ่ายไป Order อื่น</p>
      </div>
    </div>
    {showCompleteDialog && <RecordPicklistDialogConf
      orderId={currentMaterial?.pk!}
      show={showCompleteDialog}
      onHide={() => setShowCompleteDialog(false)}
      onCompleted={onCompleted}
    />}
    <Alert
      onClose={() => setShowToastError(false)}
      show={showToastError}
      variant="danger"
      dismissible
      styles='fixed -translate-x-1/2 -translate-y-1/2 bottom-8 left-1/2 z-[1000]'
    >
      <div className='flex items-center gap-3'>
        <MdBlock size={20} /> กรอกยอดใช้ดี/เสียเกินจำนวนจ่าย
      </div>
    </Alert>
  </> : <NotFound404 
    title="ไม่พบรายการจ่ายของ Component ใน Order"
    subTitle="กรุณาแจ้ง Warehouse เพื่อตรวจสอบรายการจ่ายของ Order นี้"
  />
}

export default memo(RecordPicklist)